/**
 * Other surfaces' question rows, in the shape the preview panel reads.
 *
 * Three grids in this app list questions and each carries its own row type: the
 * bank pool (`AssessmentBankQuestion`), the library hub (`LibraryItem`), and a
 * paper's own roster (`QuestionRecord`). They describe the same object with
 * different field names, different vocabularies for the same enum, and
 * different amounts of history.
 *
 * The panel could have been made generic over all three. Normalising into the
 * bank row instead is the smaller change and the one that keeps a single
 * implementation of everything downstream: `questionPreview` derives the answer
 * model, the cohort split, and the version list from a bank row, and every one
 * of those derivations is wanted on the other two surfaces too.
 *
 * **`pbi` is the load-bearing field.** `"--"` is how the model says an item has
 * never been sat, which suppresses the p-value, the distractor shares, and the
 * revision history. Both adapters map their own "never used" flag onto it, so a
 * question with no exam history reports none rather than a derived fiction.
 */

import {
  ASSESSMENT_BANK_QUESTIONS,
  bankAuthorForId,
  discriminationIndex,
  type AssessmentBankQuestion,
  type BloomLevel,
} from "@/lib/mock/assessment-add-questions"
import type { QuestionRecord } from "@/src/pages/assessment-details"
import { categoryIdsFromTopic } from "@/lib/blueprint/mapping"
import { questionCatalogById } from "@/lib/mock/question-catalog"
import type { LibraryItem } from "@/lib/mock/library"
import { POOL_TYPE_FROM_LIBRARY } from "@/lib/mock/library"
import { normalizeQuestionBankStatus } from "@/lib/question-bank-status"
import { questionPreview } from "@/lib/question-preview"
import {
  isDifficultyTag,
  toShareCorrect,
  type QuestionSignalInput,
} from "@/lib/question-signals"

/** Storage enum to the words the rest of the app uses for the same thing. */
const LIBRARY_DIFFICULTY: Record<string, AssessmentBankQuestion["difficulty"]> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
}

/**
 * A paper's roster names Bloom's levels as gerunds ("Remembering") while the
 * bank names them as verbs ("Remember"). Same taxonomy, two spellings, and the
 * icon map is keyed on the second.
 */
const BLOOM_FROM_GERUND: Record<string, BloomLevel> = {
  Remember: "Remember",
  Remembering: "Remember",
  Recall: "Remember",
  Understand: "Understand",
  Understanding: "Understand",
  Apply: "Apply",
  Applying: "Apply",
  Analyze: "Analyze",
  Analyzing: "Analyze",
  Evaluate: "Evaluate",
  Evaluating: "Evaluate",
  Create: "Create",
  Creating: "Create",
}

export function bloomFromLabel(label: string | undefined): BloomLevel | undefined {
  if (!label) return undefined
  return BLOOM_FROM_GERUND[label]
}

/**
 * A library row as the preview reads it.
 *
 * `usageCount` is how many assessments the item has been put on and
 * `examUsageCount` how many exams; the panel reports one number, so the larger
 * of the two is the honest one to show.
 */
function categoryFieldsForLibraryItem(
  item: LibraryItem,
  fallbackTopic?: string,
  canonicalIds?: readonly string[],
): Pick<AssessmentBankQuestion, "categoryIds" | "categoryUnassigned"> {
  if (item.categoryIds?.length) {
    return { categoryIds: item.categoryIds, categoryUnassigned: false }
  }
  if (item.categoryUnassigned) {
    return { categoryIds: [], categoryUnassigned: true }
  }
  if (canonicalIds?.length) return { categoryIds: [...canonicalIds] }
  return { categoryIds: categoryIdsFromTopic(item.topic || fallbackTopic) }
}

export function bankQuestionFromLibraryItem(
  item: LibraryItem,
  folderName?: string
): AssessmentBankQuestion {
  // Prefer the standing bank row so edit/preview keep the real type, stem,
  // and derived answer key instead of a lossy catalog projection.
  const canonical = ASSESSMENT_BANK_QUESTIONS.find((row) => row.id === item.id)
  if (canonical) {
    return {
      ...canonical,
      stem: item.stem || canonical.stem,
      title: item.title || canonical.title,
      difficulty: LIBRARY_DIFFICULTY[item.difficulty] ?? canonical.difficulty,
      ...(item.options?.length && (canonical.type === "Multiple Choice" || canonical.type === "Multiple Select" || canonical.type === "True or False") ? { carriedChoices: item.options } : {}),
      ...(typeof item.rationale === "string" ? { rationale: item.rationale } : {}),
      sourceLabel: folderName ?? canonical.sourceLabel,
      folderId: item.folderId || canonical.folderId,
      folderIds: item.folderIds,
      bloom: bloomFromLabel(item.bloomLevel as string | undefined) ?? canonical.bloom,
      topic: item.topic || canonical.topic,
      ...categoryFieldsForLibraryItem(item, canonical.topic, canonical.categoryIds),
      status: item.status === "Draft" ? "Draft" : item.status === "Saved" ? "Ready" : canonical.status,
    }
  }

  const timesUsed = Math.max(item.usageCount ?? 0, item.examUsageCount ?? 0)
  const pbi = item.pbi ?? item.ptBiserial
  const poolType = POOL_TYPE_FROM_LIBRARY[item.type] ?? item.type
  const optionTexts =
    item.options
      ?.map((option) => option.text.trim())
      .filter(Boolean) ?? []

  return {
    // Keep the bank id so preview and compose resolve the same item the
    // catalog row came from (fill-in answers, hotspot figures, history).
    id: item.id,
    // Falls back to the bank code rather than to the stem: a heading and a
    // subtitle that read the same are one fact printed twice.
    title: (item.title as string | undefined) ?? item.questionId,
    stem: item.stem,
    type: poolType,
    topic: item.topic,
    difficulty: LIBRARY_DIFFICULTY[item.difficulty] ?? "Medium",
    difficultyTagged: item.difficulty in LIBRARY_DIFFICULTY,
    sourceKind: "library",
    sourceLabel: folderName ?? "",
    folderId: item.folderId,
    folderIds: item.folderIds,
    ...categoryFieldsForLibraryItem(item),
    pbi: timesUsed === 0 || pbi == null ? "--" : String(pbi),
    bloom: bloomFromLabel(item.bloomLevel as string | undefined),
    author: item.author,
    authorEmail: item.authorEmail,
    tags: item.tags,
    timesUsed,
    neverUsed: timesUsed === 0,
    // Library "Saved" is Ready for the paper; Draft stays Draft.
    status: item.status === "Draft" ? "Draft" : "Ready",
    // Imported FIB stores accepted answers in `options`; choice types store
    // the keyed list the same way. Carry both so compose can seed the key.
    ...(item.type === "fill_blank" && optionTexts.length > 0
      ? { carriedFillBlankAccepted: optionTexts }
      : {}),
    ...(item.options &&
    item.options.length > 0 &&
    (item.type === "multiple_choice" ||
      item.type === "multiple_select" ||
      item.type === "true_false")
      ? { carriedChoices: item.options }
      : {}),
  }
}

/** The fields of a paper roster row the preview needs. Structural, so the
 *  adapter does not drag the page's whole `QuestionRecord` import along. */
export interface PaperQuestionRow {
  categoryIds?: string[]
  id: string
  /** Position on the paper, which is how this question is referred to here. */
  number: number
  stem: string
  title?: string
  type: string
  topic: string
  difficulty: string
  pointBiserial: number
  blooms: string
  points: number
  usedBefore: boolean
  usedCount: number
  usedInSameCourse: boolean
  /** Share correct as a whole percentage. Absent on rows that never carry one. */
  difficultyPercentage?: number
  discriminationIndex?: number
  /** Set when the roster records who wrote it. Most rows do not yet. */
  author?: string
  authorEmail?: string
  status?: "Draft" | "Ready"
}

export function bankQuestionFromPaperRow(
  row: PaperQuestionRow,
  paperName: string
): AssessmentBankQuestion {
  // A roster row rarely records who wrote the question. Falling back to the
  // faculty directory keeps the panel reading the same on a paper as it does in
  // the bank; it is mock attribution, not a recovered fact.
  const author = row.author
    ? { name: row.author, email: row.authorEmail }
    : bankAuthorForId(row.id)
  const catalog = questionCatalogById(row.id)

  return {
    // On a paper a question is known by where it sits, so that is what the
    // panel quotes rather than the row key behind it.
    id: `Q${row.number}`,
    title: row.title ?? row.stem,
    stem: row.stem,
    type: row.type,
    topic: row.topic || "Untagged",
    categoryIds: row.categoryIds,
    // Medium is the fallback the bars need, not a reading. `difficultyTagged`
    // is what stops the panel from repeating it back as the author's own tag.
    difficulty: isDifficultyTag(row.difficulty) ? row.difficulty : "Medium",
    difficultyTagged: isDifficultyTag(row.difficulty),
    sourceKind: "past",
    sourceLabel: paperName,
    folderId: "",
    pbi: row.usedBefore ? String(row.pointBiserial) : "--",
    points: row.points,
    bloom: bloomFromLabel(row.blooms),
    author: author.name,
    authorEmail: author.email,
    timesUsed: row.usedCount,
    usedInSameCourse: row.usedInSameCourse,
    neverUsed: !row.usedBefore,
    status: normalizeQuestionBankStatus(row.status),
    ...(catalog?.options?.length ? { carriedChoices: catalog.options } : {}),
  }
}

/**
 * A paper roster row as `question-signals` reads it.
 *
 * `usedBefore` is the gate on every measured field. A row that has never been
 * sat still carries `pointBiserial: 0` and `difficultyPercentage: 0`, because
 * the page defaults its numbers rather than nulling them, and passing those
 * through would band a brand new question as the worst item on the paper.
 */
export function questionSignalInputFromPaperRow(
  row: PaperQuestionRow
): QuestionSignalInput {
  const sat = row.usedBefore

  return {
    id: row.id,
    difficulty: row.difficulty,
    pValue: sat ? toShareCorrect(row.difficultyPercentage) : null,
    pbi: sat ? row.pointBiserial : null,
    dIndex: sat ? (row.discriminationIndex ?? null) : null,
    timesUsed: row.usedCount,
    usedInSameCourse: row.usedInSameCourse,
    neverUsed: !sat,
  }
}

/**
 * A bank / Add-questions row as `question-signals` reads it.
 *
 * Uses the same current-version performance the preview sheet shows, so a
 * "need review" count on the section totals cannot disagree with opening the
 * question.
 */
export function questionSignalInputFromBankRow(
  row: AssessmentBankQuestion,
  methodId?: QuestionSignalInput["methodId"]
): QuestionSignalInput {
  const { versions } = questionPreview(row)
  const current = versions.find((version) => version.current) ?? versions[0]
  const performance = current?.performance ?? {
    pValue: null,
    pbi: null,
    dIndex: null,
  }

  return {
    id: row.id,
    difficulty: row.difficultyTagged === false ? null : row.difficulty,
    pValue: performance.pValue,
    pbi: performance.pbi,
    dIndex: performance.dIndex,
    timesUsed: row.timesUsed,
    usedInSameCourse: row.usedInSameCourse,
    neverUsed: row.neverUsed,
    methodId,
  }
}

const BLOOM_GERUND: Record<BloomLevel, string> = {
  Remember: "Remembering",
  Understand: "Understanding",
  Apply: "Applying",
  Analyze: "Analyzing",
  Evaluate: "Evaluating",
  Create: "Creating",
}

/** Upper and lower 27% correct rates for a paper roster row. */
export function paperRowUpperLower(row: PaperQuestionRow): {
  upper27: number | null
  lower27: number | null
} {
  if (!row.usedBefore) return { upper27: null, lower27: null }
  const { versions } = questionPreview(bankQuestionFromPaperRow(row, "Paper"))
  const current = versions.find((version) => version.current) ?? versions[0]
  return {
    upper27: current?.performance.upper27 ?? null,
    lower27: current?.performance.lower27 ?? null,
  }
}

/** Paper roster row built from a bank row for section tables on Add questions. */
export function questionRecordFromBankRow(
  row: AssessmentBankQuestion,
  opts: { number: number; sectionId: string },
): QuestionRecord {
  const sat = row.pbi !== "--" && !row.neverUsed
  const { versions } = questionPreview(row)
  const current = versions.find((version) => version.current) ?? versions[0]
  const perf = current?.performance
  const diffPct =
    perf?.pValue != null ? Math.round(perf.pValue * 100) : 0

  return {
    id: row.id,
    number: opts.number,
    stem: row.stem,
    title: row.title,
    type: row.type,
    topic: row.topic,
    difficulty:
      row.difficultyTagged === false
        ? ""
        : row.difficulty === "Easy" ||
            row.difficulty === "Medium" ||
            row.difficulty === "Hard"
          ? row.difficulty
          : "",
    difficultyPercentage: sat ? diffPct : 0,
    pointBiserial: sat ? (perf?.pbi ?? Number(row.pbi)) : 0,
    discriminationIndex: sat
      ? (perf?.dIndex ?? discriminationIndex(row) ?? 0)
      : 0,
    blooms: row.bloom ? BLOOM_GERUND[row.bloom] : "",
    points: row.points ?? 1,
    usedBefore: sat,
    usedCount: row.timesUsed ?? 0,
    usedInSameCourse: row.usedInSameCourse ?? false,
    sectionId: opts.sectionId,
    status: normalizeQuestionBankStatus(row.status),
    isNew: row.isNew,
    author: row.author,
    authorEmail: row.authorEmail,
  }
}
